import { FunctionComponent, useEffect, useState } from 'react';

import MenuIcon from '@mui/icons-material/Menu';
// eslint-disable-next-line import/order
import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';

import { Link } from 'react-router-dom';

import { PAPERTRADING_COLOR } from '../../../assets/colors';
import { ACCOUNT_CODE_PAPERTRADING } from '../../../assets/constants';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getPriceAlertsAsync } from '../../../store/price-alert/service';
import { getSystemProfileAsync } from '../../../store/system-profile/service';
import BrokerStatusMenuButton from '../../ui/BrokerStatusMenuButton';
import ScheduleStatusMenuButton from '../../ui/ScheduleStatusMenuButton';

import AccountMenu from './AccountMenu';
import Clock from './Clock';

import './styles.scss';

interface NavItem {
  name: string;
  to: string;
}

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
}

const Header: FunctionComponent<Props> = ({ window }: Props) => {
  const theme = useTheme();
  const isTabletViewPort = useMediaQuery(theme.breakpoints.down('md'));
  const { loaded, webApiVersion } = useAppSelector((gs) => gs.bootstrapDataState);
  const { priceAlerts } = useAppSelector((gs) => gs.priceAlertState);
  const { loaded: systemProfileLoaded } = useAppSelector((gs) => gs.systemProfileState);
  const { accountInfo } = useAppSelector((gs) => gs.accountInfoState);

  const dispatch = useAppDispatch();

  const isPaperTrading = accountInfo?.accountCode === ACCOUNT_CODE_PAPERTRADING;

  useEffect(() => {
    if (!systemProfileLoaded) {
      dispatch(getSystemProfileAsync());
    }
  }, [systemProfileLoaded]);

  useEffect(() => {
    if (!loaded) {
      dispatch(getPriceAlertsAsync());
    }
  }, []);

  const navItems: NavItem[] = [
    { name: 'Dashboard', to: '/' },
    { name: 'Watchlist', to: '/watchlist' },
    { name: 'Securities', to: '/securities' },
    { name: 'History', to: '/history' }
  ];
  const drawerWidth = 240;

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const titleEl = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25
      }}
    >
      <Typography sx={{ py: 0, fontSize: 20, fontFamily: 'Oxanium', lineHeight: 1 }}>P1Trader</Typography>
      {loaded && (
        <Tooltip title="Web Api Version">
          <Typography variant="caption" sx={{ lineHeight: 1 }}>{`v${webApiVersion}`}</Typography>
        </Tooltip>
      )}
    </Box>
  );

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ m: 2 }}>{titleEl}</Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }}>
              <Link to={item.to} className="link-item">
                <ListItemText primary={item.name} />
              </Link>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  const buttonLinks = navItems.map((item) => {
    const label =
      item.name === 'Watchlist' ? (
        <Badge badgeContent={priceAlerts.length} color="error" variant="dot">
          {item.name}
        </Badge>
      ) : (
        <span>{item.name}</span>
      );
    return (
      <Button key={item.name}>
        <Link to={item.to} className="link-item">
          {label}
        </Link>
      </Button>
    );
  });

  const toolbar = isTabletViewPort ? (
    <Toolbar sx={{ justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
        <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle}>
          <MenuIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BrokerStatusMenuButton />
        <ScheduleStatusMenuButton />
        <AccountMenu />
        <Clock ml={2} />
      </Box>
    </Toolbar>
  ) : (
    <Toolbar sx={{ justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
        {titleEl}
        {buttonLinks}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BrokerStatusMenuButton />
        <ScheduleStatusMenuButton />
        <AccountMenu />
        <Clock ml={2} />
      </Box>
    </Toolbar>
  );

  return (
    <Box sx={{ display: 'column' }}>
      <AppBar component="nav">
        {toolbar}
        {isPaperTrading && (
          <Box
            sx={{
              height: 18,
              bgcolor: PAPERTRADING_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography sx={{ fontSize: '7pt', letterSpacing: 1 }}>PAPER TRADING</Typography>
          </Box>
        )}
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      <Toolbar />
      {isPaperTrading && <Box sx={{ height: 18 }} />}
    </Box>
  );
};

export default Header;
